import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Badge } from "@/lib/ui";
import Avatar from "@/components/Avatar";
import { Siren, ShieldAlert, Footprints, PhoneCall, RefreshCw } from "lucide-react";
import AdminEmailQueue from "@/components/AdminEmailQueue";

const ago = (iso) => { const m = Math.round((Date.now() - new Date(iso)) / 60000); return m < 60 ? `${m} min ago` : `${Math.floor(m / 60)} h ${m % 60} min ago`; };

function Person({ s }) {
  return (
    <div className="flex items-center gap-3">
      <Avatar user={s} size="sm" color="bg-[#D96B43]" />
      <div>
        <p className="font-semibold text-slate-900">{s.name}</p>
        <p className="text-sm text-slate-600">{s.phone || "no phone"} · {s.address || "no address"}</p>
        {s.contact?.name && <p className="text-sm text-slate-600 flex items-center gap-1"><PhoneCall className="w-3.5 h-3.5" /> {s.contact.name} {s.contact.phone || s.contact.email}</p>}
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, count, color, children, testid }) {
  return (
    <section data-testid={testid} className="bg-white border border-[#E2D9CE] rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4"><Icon className={`w-6 h-6 ${color}`} /><h2 className="text-xl font-display font-bold text-slate-900">{title}</h2><Badge color="slate">{count}</Badge></div>
      {children}
    </section>
  );
}

export default function AdminSafetyDesk() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/safety").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const i = setInterval(load, 15000); return () => clearInterval(i); }, []);

  const act = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); toast.success(ok); await load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!data) return <p className="text-slate-600">Loading safety desk…</p>;
  const empty = (msg, testid) => <p data-testid={testid} className="text-slate-500">{msg}</p>;

  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-500 flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Live · refreshes every 15 seconds · updated {new Date(data.generated_at).toLocaleTimeString()}</p>

      <Section icon={Siren} title="Active SOS alerts" count={data.sos.length} color="text-[#C62828]" testid="safety-sos">
        {data.sos.length === 0 ? empty("No active emergencies. 🙏", "safety-sos-empty") : data.sos.map((s) => (
          <div key={s.id} data-testid={`sos-item-${s.id}`} className="border-2 border-[#C62828]/40 bg-[#FFF5F5] rounded-xl p-4 mb-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <Person s={s.seeker} />
              <div className="text-right">
                <p className="font-bold text-[#C62828]">{ago(s.created_at)}</p>
                <p className="text-sm text-slate-600">Family: {s.family} · Helpers emailed: {s.helpers.filter((h) => h.status === "sent").length}</p>
              </div>
            </div>
            {s.note && <p className="mt-2 text-slate-800 italic">“{s.note}”</p>}
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
              {s.helper_name ? <Badge color="green">{s.helper_name} accepted</Badge> : <Badge color="gold">No helper yet</Badge>}
              {s.lat != null && <a className="underline text-[#1F5F4F]" href={`https://www.google.com/maps?q=${s.lat},${s.lng}`} target="_blank" rel="noreferrer">Map</a>}
              <Link className="underline text-[#1F5F4F]" to={`/requests/${s.request_id}`}>Request</Link>
            </div>
            <button data-testid={`sos-resolve-${s.id}`} disabled={busy === s.id}
              onClick={() => act(s.id, () => api.post(`/admin/sos/${s.id}/resolve`, { note: "Resolved by admin after follow-up" }), "SOS marked resolved")}
              className="mt-3 bg-[#2E7D6B] hover:bg-[#256658] disabled:opacity-60 text-white font-semibold px-4 min-h-[44px] rounded-xl">Mark resolved</button>
          </div>
        ))}
      </Section>

      <Section icon={ShieldAlert} title="Overdue safety check-ins" count={data.overdue.length} color="text-[#A9741A]" testid="safety-overdue">
        {data.overdue.length === 0 ? empty("Everyone has checked in.", "safety-overdue-empty") : data.overdue.map((r) => (
          <div key={r.id} data-testid={`overdue-item-${r.id}`} className="border border-[#F1D9A3] bg-[#FFF7E6] rounded-xl p-4 mb-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <Person s={r.seeker} />
              <p className="text-sm text-slate-700 mt-2">{r.title} · helper {r.helper_name} · completed {ago(r.completed_at)} · <Badge color={r.safety_status === "alerted" ? "red" : "gold"}>{r.safety_status.replace("_", " ")}</Badge></p>
            </div>
            <button data-testid={`overdue-safe-${r.id}`} disabled={busy === r.id}
              onClick={() => act(r.id, () => api.post(`/admin/requests/${r.id}/safe`, { note: "Confirmed safe by phone" }), "Marked safe")}
              className="bg-[#2E7D6B] hover:bg-[#256658] disabled:opacity-60 text-white font-semibold px-4 min-h-[44px] rounded-xl">Mark safe (spoke to them)</button>
          </div>
        ))}
      </Section>

      <Section icon={Footprints} title="Visits in progress" count={data.in_progress.length} color="text-[#2E7D6B]" testid="safety-inprogress">
        {data.in_progress.length === 0 ? empty("No helpers are with a seeker right now.", "safety-inprogress-empty") : data.in_progress.map((r) => (
          <div key={r.id} data-testid={`inprogress-item-${r.id}`} className="border border-[#B5DBD1] bg-[#E8F4F1] rounded-xl p-4 mb-3 flex flex-wrap items-center justify-between gap-3">
            <Person s={r.seeker} />
            <p className="text-sm text-slate-700">{r.title} · {r.helper_name} {r.arrived_at ? `arrived ${ago(r.arrived_at)}` : "in progress"} · {r.location}</p>
          </div>
        ))}
      </Section>

      <AdminEmailQueue />
    </div>
  );
}
